import React, { useEffect, useState } from "react";
import { theme } from "../theme";

const messages = [
  "Initializing anomaly detection engine...",
  "Parsing dataset features...",
  "Training Isolation Forest model...",
  "Scanning for anomalous patterns...",
  "Computing severity scores...",
  "Correlating attack signatures...",
  "Building threat profile...",
  "Finalizing analysis report...",
];

export default function LoadingOverlay({ visible, fileName }) {
  const [msgIndex, setMsgIndex] = useState(0);
  const [dots, setDots]         = useState(0);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (!visible) {
      setMsgIndex(0);
      setProgress(0);
      return;
    }
    // Rotate status messages
    const msgTimer = setInterval(() => {
      setMsgIndex(i => (i + 1) % messages.length);
    }, 1800);
    // Animate dots
    const dotTimer = setInterval(() => {
      setDots(d => (d + 1) % 4);
    }, 400);
    // Fake progress bar (never reaches 100 until done)
    let p = 0;
    const progTimer = setInterval(() => {
      p += Math.random() * 4;
      if (p > 92) p = 92;
      setProgress(Math.floor(p));
    }, 300);

    return () => {
      clearInterval(msgTimer);
      clearInterval(dotTimer);
      clearInterval(progTimer);
    };
  }, [visible]);

  if (!visible) return null;

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 9999,
      background: theme.colors.overlay,
      backdropFilter: "blur(8px)",
      display: "flex", alignItems: "center", justifyContent: "center",
      flexDirection: "column"
    }}>

      {/* ── Sandclock SVG ── */}
      <div style={{ marginBottom: 36, position: "relative" }}>

        {/* Outer glow ring */}
        <div style={{
          position: "absolute",
          width: 140, height: 140,
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(0,212,255,0.12) 0%, transparent 70%)",
          top: "50%", left: "50%",
          transform: "translate(-50%, -50%)",
          animation: "pulse-glow 2s ease-in-out infinite"
        }} />

        <svg width="100" height="130" viewBox="0 0 100 130" fill="none" xmlns="http://www.w3.org/2000/svg">
          <defs>
            {/* Sand gradient */}
            <linearGradient id="sandGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#FFB800" stopOpacity="0.9" />
              <stop offset="100%" stopColor="#FF8C00" stopOpacity="0.7" />
            </linearGradient>
            {/* Glass gradient */}
            <linearGradient id="glassGrad" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#00D4FF" stopOpacity="0.15" />
              <stop offset="50%" stopColor="#00D4FF" stopOpacity="0.05" />
              <stop offset="100%" stopColor="#00D4FF" stopOpacity="0.15" />
            </linearGradient>
            <clipPath id="topClip">
              <polygon points="15,12 85,12 52,64 48,64" />
            </clipPath>
            <clipPath id="bottomClip">
              <polygon points="48,66 52,66 85,118 15,118" />
            </clipPath>
            {/* Animated sand fall */}
            <style>{`
              @keyframes sandFall {
                0%   { transform: translateY(-8px); opacity: 0; }
                20%  { opacity: 1; }
                80%  { opacity: 1; }
                100% { transform: translateY(8px); opacity: 0; }
              }
              @keyframes sandFillBottom {
                0%   { d: path("M48,118 L52,118 L52,118 L48,118 Z"); }
                100% { d: path("M15,118 L85,118 L52,90 L48,90 Z"); }
              }
              @keyframes hourglassRock {
                0%,100% { transform: rotate(0deg); }
                25%     { transform: rotate(-2deg); }
                75%     { transform: rotate(2deg); }
              }
              @keyframes glowPulse {
                0%,100% { opacity: 0.6; }
                50%      { opacity: 1; }
              }
              @keyframes topSandDrain {
                0%   { transform: scaleY(1);   transform-origin: bottom; }
                100% { transform: scaleY(0.1); transform-origin: bottom; }
              }
              @keyframes bottomSandFill {
                0%   { transform: scaleY(0.1); transform-origin: bottom; }
                100% { transform: scaleY(1);   transform-origin: bottom; }
              }
              @keyframes streamFall {
                0%   { opacity: 0; transform: translateY(0); }
                10%  { opacity: 1; }
                90%  { opacity: 1; }
                100% { opacity: 0; transform: translateY(4px); }
              }
            `}</style>
          </defs>

          {/* Hourglass frame — rocking */}
          <g style={{ animation: "hourglassRock 4s ease-in-out infinite", transformOrigin: "50px 65px" }}>

            {/* Top frame bar */}
            <rect x="10" y="6" width="80" height="10" rx="5"
              fill="none" stroke="#00D4FF" strokeWidth="2.5" opacity="0.8" />
            {/* Bottom frame bar */}
            <rect x="10" y="114" width="80" height="10" rx="5"
              fill="none" stroke="#00D4FF" strokeWidth="2.5" opacity="0.8" />
            {/* Left side connector */}
            <line x1="14" y1="16" x2="14" y2="114" stroke="#00D4FF" strokeWidth="1.5" opacity="0.3" />
            {/* Right side connector */}
            <line x1="86" y1="16" x2="86" y2="114" stroke="#00D4FF" strokeWidth="1.5" opacity="0.3" />

            {/* Top glass shape */}
            <polygon points="15,16 85,16 52,63 48,63"
              fill="url(#glassGrad)" stroke="#00D4FF" strokeWidth="1.5" strokeOpacity="0.7" />

            {/* Bottom glass shape */}
            <polygon points="48,67 52,67 85,114 15,114"
              fill="url(#glassGrad)" stroke="#00D4FF" strokeWidth="1.5" strokeOpacity="0.7" />

            {/* Top sand — draining animation */}
            <g clipPath="url(#topClip)">
              <polygon points="15,16 85,16 52,63 48,63"
                fill="url(#sandGrad)" opacity="0.85"
                style={{ animation: "topSandDrain 3s ease-in-out infinite alternate", transformOrigin: "50px 63px" }}
              />
            </g>

            {/* Bottom sand — filling animation */}
            <g clipPath="url(#bottomClip)">
              <polygon points="48,114 52,114 52,114 48,114"
                fill="url(#sandGrad)" opacity="0.85"
                style={{ animation: "bottomSandFill 3s ease-in-out infinite alternate", transformOrigin: "50px 114px" }}
              />
            </g>

            {/* Sand stream — falling through neck */}
            <rect x="49" y="60" width="2" height="10" rx="1"
              fill="#FFB800" opacity="0.9"
              style={{ animation: "streamFall 0.6s ease-in-out infinite" }} />
            <rect x="49.5" y="60" width="1" height="10" rx="0.5"
              fill="#FFF" opacity="0.5"
              style={{ animation: "streamFall 0.6s ease-in-out infinite 0.1s" }} />

            {/* Neck pinch */}
            <ellipse cx="50" cy="65" rx="3" ry="2"
              fill="#050D1A" stroke="#00D4FF" strokeWidth="1" strokeOpacity="0.5" />

            {/* Shimmer on top glass */}
            <line x1="25" y1="20" x2="45" y2="55"
              stroke="#fff" strokeWidth="1" opacity="0.08" />

            {/* Shimmer on bottom glass */}
            <line x1="25" y1="110" x2="45" y2="75"
              stroke="#fff" strokeWidth="1" opacity="0.08" />

            {/* Corner accent dots */}
            <circle cx="10" cy="11"  r="3" fill="#00D4FF" opacity="0.8" style={{ animation: "glowPulse 2s ease-in-out infinite" }} />
            <circle cx="90" cy="11"  r="3" fill="#00D4FF" opacity="0.8" style={{ animation: "glowPulse 2s ease-in-out infinite 0.5s" }} />
            <circle cx="10" cy="119" r="3" fill="#00D4FF" opacity="0.8" style={{ animation: "glowPulse 2s ease-in-out infinite 1s" }} />
            <circle cx="90" cy="119" r="3" fill="#00D4FF" opacity="0.8" style={{ animation: "glowPulse 2s ease-in-out infinite 1.5s" }} />
          </g>
        </svg>
      </div>

      {/* ── Title ── */}
      <div style={{
        fontFamily: theme.fonts.display,
        fontSize: 20, fontWeight: 900,
        color: theme.colors.accent,
        letterSpacing: 3,
        marginBottom: 8
      }}>
        ANALYZING{".".repeat(dots)}
      </div>

      {/* ── Dataset name ── */}
      {fileName && (
        <div style={{
          fontFamily: theme.fonts.mono,
          fontSize: 11, color: theme.colors.textSecondary,
          marginBottom: 24, letterSpacing: 1
        }}>
          ▸ {fileName}
        </div>
      )}

      {/* ── Status message ── */}
      <div style={{
        fontFamily: theme.fonts.mono,
        fontSize: 12, color: theme.colors.textMuted,
        letterSpacing: 1, marginBottom: 28,
        minHeight: 20, textAlign: "center",
        transition: "opacity 0.4s",
        maxWidth: 340
      }}>
        {messages[msgIndex]}
      </div>

      {/* ── Progress bar ── */}
      <div style={{ width: 300, position: "relative" }}>
        {/* Track */}
        <div style={{
          width: "100%", height: 6,
          background: theme.colors.cardBorder,
          borderRadius: 3, overflow: "hidden"
        }}>
          {/* Fill */}
          <div style={{
            height: "100%",
            width: `${progress}%`,
            background: `linear-gradient(90deg, ${theme.colors.accent}, #00FFAA)`,
            borderRadius: 3,
            transition: "width 0.3s ease",
            boxShadow: `0 0 8px rgba(0,212,255,0.6)`
          }} />
        </div>
        {/* Percentage */}
        <div style={{
          position: "absolute", right: 0, top: 12,
          fontFamily: theme.fonts.mono, fontSize: 10,
          color: theme.colors.textMuted
        }}>
          {progress}%
        </div>
      </div>

      {/* ── Scan lines decoration ── */}
      <div style={{
        position: "absolute", inset: 0, pointerEvents: "none",
        backgroundImage: "repeating-linear-gradient(0deg, transparent, transparent 3px, rgba(0,212,255,0.015) 3px, rgba(0,212,255,0.015) 4px)"
      }} />

    </div>
  );
}
